import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Popover from "@mui/material/Popover";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SoftSelect from "components/SoftSelect";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";

import Table from "examples/Tables/Table";
import ResourceRequestForm from "./Users/components/forms/ResourceRequestForm";
import { useAuth } from "context/AuthContext";
import {
  getResourceRequests,
  getResourceRequestById,
  saveResourceRequest,
} from "./Users/services/graphqlService";
import { saveResourceRequestWithImage } from "utils/missionDocuments";
import { getRequestDisplayLocations } from "./resourceRequestLocations";
import {
  canEditRequestForProfile,
  getRequestEditDeniedMessage,
  isRequestOwner,
} from "./requestOwnerUtils";

function normalizeRequestsList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  // Parse if API returns stringified JSON
  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return [];
    }
  }
  if (!parsed || typeof parsed !== "object") return [];

  const arrayKeys = [
    "ResourceRequestsList",
    "ResourceRequests",
    "resourceRequests",
    "requests",
    "items",
    "data",
    "list",
    "results",
    "records",
    "content",
    "rows",
    "AirportsList",
  ];
  for (const key of arrayKeys) {
    if (Array.isArray(parsed[key])) return parsed[key];
  }
  // Single resource request object (some APIs return one record)
  if (parsed.requestId || parsed.id || parsed.title) {
    return [parsed];
  }
  return [];
}

const FILTER_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "Medical", label: "Medical" },
  { value: "Animal Rescue", label: "Animal Rescue" },
  { value: "Disaster Relief", label: "Disaster Relief" },
  { value: "Veteran Support", label: "Veteran Support" },
  { value: "Conservation", label: "Conservation" },
  { value: "Aviation Experience", label: "Aviation Experience" },
  { value: "other", label: "Others" },
];

const FILTER_DUE_DATE_OPTIONS = [
  { value: "", label: "All upcoming" },
  { value: "overdue", label: "Past due (mine)" },
  { value: "this_week", label: "Due this week" },
  { value: "this_month", label: "Due this month" },
];
/** Local calendar day for dueDate (handles YYYY-MM-DD without UTC shift). */
function getRequestDueDay(dueDate) {
  if (!dueDate) return null;
  const raw = String(dueDate).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function isRequestPastDue(request) {
  const dueDay = getRequestDueDay(request?.dueDate);
  if (!dueDay) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDay < today;
}

function RequestsPage() {
  const { user, profile, tokenRole, tokenEntityType, isGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [requests, setRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterOriginState, setFilterOriginState] = useState("");
  const [filterDestinationState, setFilterDestinationState] = useState("");
  const [filterDueDate, setFilterDueDate] = useState("");
  const [filterUrgentOnly, setFilterUrgentOnly] = useState(false);
  const [sortBy, setSortBy] = useState("title");
  const [sortOrder, setSortOrder] = useState("asc");
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [pendingDeleteDraftRequest, setPendingDeleteDraftRequest] =
    useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);
  const [pageToken, setPageToken] = useState(null);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenHistory, setTokenHistory] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const editDeniedMessage = getRequestEditDeniedMessage({
    tokenRole,
    tokenEntityType,
    isGuest,
  });
  const canEditRequest = (request) =>
    canEditRequestForProfile(request, {
      profileId: profile?.profileId,
      tokenRole,
      tokenEntityType,
      isGuest,
    });

  // Open edit form when navigating from View page with editRequestId
  useEffect(() => {
    const editId = location.state?.editRequestId;
    if (!editId) return;
    navigate(location.pathname, { replace: true, state: {} });
    handleEdit(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.editRequestId]);

  const fetchRequestsPage = useCallback(
    async (token = null, { isInitial = false } = {}) => {
      if (!user?.id_token) {
        setLoadingList(false);
        setPageLoading(false);
        setRequests([]);
        setNextPageToken(null);
        return;
      }
      if (isInitial) setLoadingList(true);
      else setPageLoading(true);
      try {
        const res = await getResourceRequests(user.id_token, token);
        const list = normalizeRequestsList(res?.data);
        setRequests(Array.isArray(list) ? list : []);
        setNextPageToken(res?.nextToken ?? null);
        setPageToken(token);
      } catch {
        setRequests([]);
        setNextPageToken(null);
      } finally {
        setLoadingList(false);
        setPageLoading(false);
      }
    },
    [user?.id_token],
  );

  const fetchRequestsList = useCallback(async () => {
    setTokenHistory([]);
    setPageToken(null);
    setNextPageToken(null);
    await fetchRequestsPage(null, { isInitial: true });
  }, [fetchRequestsPage]);

  const handleNextPage = async () => {
    if (!nextPageToken || pageLoading) return;
    setTokenHistory((prev) => [...prev, pageToken]);
    await fetchRequestsPage(nextPageToken);
  };

  const handlePreviousPage = async () => {
    if (tokenHistory.length === 0 || pageLoading) return;
    const prevHistory = [...tokenHistory];
    const previousToken = prevHistory.pop();
    setTokenHistory(prevHistory);
    await fetchRequestsPage(previousToken ?? null);
  };

  useEffect(() => {
    fetchRequestsList();
  }, [fetchRequestsList]);

  const canGoPrevious = tokenHistory.length > 0;
  const canGoNext = Boolean(nextPageToken);

  const handleEdit = async (requestId) => {
    if (!requestId) return;
    setError(null);
    if (!user?.id_token) return;
    setLoading(true);
    try {
      const res = await getResourceRequestById(requestId, user.id_token);
      const data = res?.data;
      if (!canEditRequest(data)) {
        setError(editDeniedMessage);
        return;
      }
      setInitialData(data);
      setEditingRequestId(requestId);
      setShowForm(true);
    } catch (err) {
      setError(err.message || "Failed to load resource request.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    requestData,
    { pendingImageFiles = [], onUploadProgress } = {},
  ) => {
    if (!user?.id_token) return;
    if (editingRequestId && !canEditRequest(initialData)) {
      setError(editDeniedMessage);
      throw new Error(editDeniedMessage);
    }
    const isDraft =
      String(requestData.status || "")
        .trim()
        .toLowerCase() === "draft";
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    // Dispatch processing notification - success will come from subscription (like AddEntity)
    window.dispatchEvent(
      new CustomEvent("globalNotification", {
        detail: {
          type: "info",
          title: "Processing",
          message: isDraft
            ? "Saving draft... Please wait for confirmation."
            : "Saving resource request... Please wait for confirmation.",
          removeOnSuccess: true,
        },
      }),
    );

    try {
      const result = await saveResourceRequestWithImage(
        requestData,
        user.id_token,
        editingRequestId || undefined,
        {
          pendingImageFiles,
          onFileProgress: (name, percent) => onUploadProgress?.(name, percent),
        },
      );
      if (result.success) {
        // Success from subscription (backend publishes to profileId); fallback if EventId mismatch
        const savedId =
          result.requestId ??
          result.data?.requestId ??
          result.data?.saveResourceRequest?.requestId ??
          editingRequestId ??
          null;

        setShowForm(false);
        setEditingRequestId(null);
        setInitialData(null);
        setSuccessMessage(
          result.message ||
            (isDraft
              ? "Draft saved successfully."
              : "Resource request saved successfully."),
        );

        if (savedId && !isDraft) {
          navigate(`/dashboard/requests/${savedId}`);
        } else {
          await fetchRequestsList();
          window.setTimeout(() => {
            fetchRequestsList();
          }, 600);
          window.setTimeout(() => {
            fetchRequestsList();
          }, 2000);
        }
        // Fallback snackbar in case backend publishes with different EventId
        window.dispatchEvent(
          new CustomEvent("globalNotification", {
            detail: {
              type: "success",
              title: "Success",
              message:
                result.message ||
                (isDraft
                  ? "Draft saved successfully."
                  : "Resource request saved successfully."),
            },
          }),
        );
      } else {
        setError(result.message || "Failed to save resource request.");
      }
    } catch (err) {
      setError(err.message || "Failed to save resource request.");
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message: err.message || "Failed to save resource request.",
          },
        }),
      );
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRequestId(null);
    setInitialData(null);
    setError(null);
  };

  const handleDeleteDraft = async () => {
    const pending = pendingDeleteDraftRequest;
    if (!pending?.id || !user?.id_token || deletingDraft) return;

    setDeletingDraft(true);
    setError(null);
    try {
      // Re-fetch before mutating so a request that has since been published
      // cannot be deleted from stale list data.
      const result = await getResourceRequestById(pending.id, user.id_token);
      const request = result?.data;
      const isStillDraft =
        String(request?.status || "")
          .trim()
          .toLowerCase() === "draft";

      if (!isStillDraft) {
        throw new Error("Only draft resource requests can be deleted.");
      }
      if (!canEditRequest(request)) {
        throw new Error(editDeniedMessage);
      }

      const saveResult = await saveResourceRequest(
        { status: "Inactive" },
        user.id_token,
        pending.id,
      );
      if (!saveResult.success) {
        throw new Error(saveResult.message || "Failed to delete draft.");
      }

      setRequests((current) =>
        current.filter(
          (requestItem) =>
            String(requestItem.requestId || requestItem.id) !==
            String(pending.id),
        ),
      );
      setPendingDeleteDraftRequest(null);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "success",
            title: "Success",
            message: "Draft deleted successfully.",
          },
        }),
      );
    } catch (err) {
      const message = err.message || "Failed to delete draft.";
      setError(message);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message,
          },
        }),
      );
    } finally {
      setDeletingDraft(false);
    }
  };

  const handleAddNew = () => {
    setEditingRequestId(null);
    setInitialData(null);
    setError(null);
    setSuccessMessage(null);
    setShowForm(true);
  };

  const hasActiveFilters =
    !!filterType ||
    !!filterOriginState ||
    !!filterDestinationState ||
    !!filterDueDate ||
    filterUrgentOnly;

  const visibleRequests = requests.filter(
    (r) =>
      String(r.status || "")
        .trim()
        .toLowerCase() !== "inactive",
  );

  /** API often omits flat location fields; route is stored under description [Route]. */
  const displayRequests = useMemo(
    () =>
      visibleRequests.map((r) => ({
        ...r,
        ...getRequestDisplayLocations(r),
      })),
    [visibleRequests],
  );

  const uniqueOriginStates = [
    ...new Set(
      displayRequests
        .map((r) => r.locationState)
        .filter((s) => s && !["Yes", "No"].includes(s)),
    ),
  ].sort();
  const uniqueDestinationStates = [
    ...new Set(
      displayRequests
        .map((r) => r.arrivalState)
        .filter((s) => s && !["Yes", "No"].includes(s)),
    ),
  ].sort();

  const isRequestUrgent = (r) =>
    r.isAsap === true ||
    (typeof r.isAsap === "string" &&
      ["yes", "true"].includes(r.isAsap.toLowerCase()));

  const filteredRequests = displayRequests.filter((r) => {
    const pastDue = isRequestPastDue(r);
    const ownsRequest = isRequestOwner(r, profile?.profileId);

    if (filterType) {
      const knownTypes = [
        "medical",
        "animal rescue",
        "disaster relief",
        "veteran support",
        "conservation",
        "aviation experience",
      ];
      const rType = (r.type || "").toLowerCase();
      const matchType =
        filterType === "other"
          ? rType && !knownTypes.includes(rType)
          : rType === filterType.toLowerCase();
      if (!matchType) return false;
    }
    if (
      filterOriginState &&
      (r.locationState || "").toLowerCase() !== filterOriginState.toLowerCase()
    )
      return false;
    if (
      filterDestinationState &&
      (r.arrivalState || "").toLowerCase() !==
        filterDestinationState.toLowerCase()
    )
      return false;
    if (filterUrgentOnly && !isRequestUrgent(r)) return false;
    if (filterDueDate) {
      const dueDay = getRequestDueDay(r.dueDate);
      if (!dueDay) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (filterDueDate === "overdue") {
        if (!pastDue || !ownsRequest) return false;
      }
      if (filterDueDate === "this_week") {
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);
        if (dueDay < today || dueDay > weekEnd) return false;
      }
      if (filterDueDate === "this_month") {
        const monthEnd = new Date(today);
        monthEnd.setDate(monthEnd.getDate() + 30);
        if (dueDay < today || dueDay > monthEnd) return false;
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const title = (r.title || "").toLowerCase();
      const description = (r.description || "").toLowerCase();
      const type = (r.type || "").toLowerCase();
      const requester = (r.requesterName || "").toLowerCase();
      const arrivalCity = (r.arrivalCity || "").toLowerCase();
      const locationCity = (r.locationCity || "").toLowerCase();
      const matchesSearch =
        title.includes(q) ||
        description.includes(q) ||
        type.includes(q) ||
        requester.includes(q) ||
        arrivalCity.includes(q) ||
        locationCity.includes(q);
      if (!matchesSearch) return false;
    }
    return true;
  });

  const formatCityState = (city, state) => {
    if (!city && !state) return "—";
    return [city || "", state || ""].filter(Boolean).join(", ");
  };

  const getSortValue = (req, key) => {
    switch (key) {
      case "title":
        return (req.title || "").toLowerCase();
      case "type":
        return (req.type || "").toLowerCase();
      case "origin":
        return formatCityState(
          req.locationCity,
          req.locationState,
        ).toLowerCase();
      case "destination":
        return formatCityState(req.arrivalCity, req.arrivalState).toLowerCase();
      case "requester":
        return (req.requesterName || "").toLowerCase();
      default:
        return "";
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const va = getSortValue(a, sortBy);
    const vb = getSortValue(b, sortBy);
    const cmp = va.localeCompare(vb, undefined, { numeric: true });
    return sortOrder === "asc" ? cmp : -cmp;
  });

  const requestColumns = [
    { name: "title", align: "left", sortable: true },
    { name: "type", align: "left", sortable: true },
    { name: "origin", align: "left", sortable: true },
    { name: "destination", align: "left", sortable: true },
    { name: "requester", align: "left", sortable: true },
    { name: "actions", align: "left" },
  ];

  const requestRows = sortedRequests.map((req, idx) => {
    const requestId = req.requestId || req.id || idx;
    const statusValue = (req.status || "").toString().trim().toLowerCase();
    const isDraftRequest =
      statusValue === "draft" || statusValue.includes("draft");
    const editable = canEditRequest(req);
    return {
      title: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <SoftTypography variant="button" fontWeight="medium">
            {req.title || "Untitled request"}
          </SoftTypography>
          {isDraftRequest && editable && (
            <SoftTypography variant="caption" color="warning" fontWeight="bold">
              Draft
            </SoftTypography>
          )}
          {isRequestPastDue(req) && (
            <SoftTypography variant="caption" color="error" fontWeight="bold">
              Past due
            </SoftTypography>
          )}
        </SoftBox>
      ),
      type: (
        <SoftTypography variant="caption" color="secondary">
          {req.type || "—"}
        </SoftTypography>
      ),
      origin: (
        <SoftTypography variant="caption" color="secondary">
          {formatCityState(req.locationCity, req.locationState)}
        </SoftTypography>
      ),
      destination: (
        <SoftTypography variant="caption" color="secondary">
          {formatCityState(req.arrivalCity, req.arrivalState)}
        </SoftTypography>
      ),
      requester: (
        <SoftTypography variant="caption" color="secondary">
          {req.requesterName || "—"}
        </SoftTypography>
      ),
      actions: (
        <SoftBox
          display="flex"
          gap={1}
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          sx={{ minWidth: 230 }}
        >
          <SoftButton
            variant="text"
            color="info"
            size="small"
            onClick={() => navigate(`/dashboard/requests/${requestId}`)}
          >
            <Icon>visibility</Icon>&nbsp;View
          </SoftButton>
          {editable && (
            <SoftButton
              variant="text"
              color="dark"
              size="small"
              onClick={() => handleEdit(requestId)}
            >
              <Icon>edit</Icon>&nbsp;Edit
            </SoftButton>
          )}
          {isDraftRequest && editable && (
            <SoftButton
              variant="text"
              color="error"
              size="small"
              onClick={() =>
                setPendingDeleteDraftRequest({
                  id: String(requestId),
                  title: req.title || "Untitled request",
                })
              }
            >
              <Icon>delete_outline</Icon>&nbsp;Delete
            </SoftButton>
          )}
        </SoftBox>
      ),
    };
  });

  return (
    <SoftBox py={3}>
      <SoftBox
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="bold" gutterBottom>
            Flight Requests
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Create and manage flight requests for cargo, passengers, and relief.
          </SoftTypography>
        </SoftBox>
        {!showForm && (
          <SoftButton variant="gradient" color="info" onClick={handleAddNew}>
            <Icon sx={{ mr: 1 }}>add</Icon>
            Add Request
          </SoftButton>
        )}
      </SoftBox>

      {successMessage && (
        <SoftBox mb={2}>
          <Alert severity="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        </SoftBox>
      )}

      {error && (
        <SoftBox mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </SoftBox>
      )}

      {showForm ? (
        <Card sx={{ overflow: "visible" }}>
          <SoftBox p={3} sx={{ overflow: "visible" }}>
            <SoftTypography variant="h6" fontWeight="bold" gutterBottom>
              {editingRequestId ? "Edit Flight Request" : "New Flight Request"}
            </SoftTypography>
            <ResourceRequestForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              initialData={initialData}
              loading={loading}
              idToken={user?.id_token}
            />
          </SoftBox>
        </Card>
      ) : (
        <Card>
          <SoftBox p={3}>
            {loadingList ? (
              <SoftBox display="flex" alignItems="center" gap={1} py={2}>
                <CircularProgress size={24} />
                <SoftTypography variant="body2">
                  Loading requests...
                </SoftTypography>
              </SoftBox>
            ) : visibleRequests.length === 0 ? (
              <SoftTypography variant="body2" color="text">
                No flight requests yet. Use &quot;Add Request&quot; to create
                one with location, cargo/passenger details, and coordinator
                info.
              </SoftTypography>
            ) : (
              <>
                <SoftBox
                  mb={2}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={1}
                >
                  <SoftBox
                    sx={{
                      width: 360,
                      maxWidth: "100%",
                      "&:focus-within": {
                        "& .MuiInputBase-root": {
                          border: "2px solid",
                          borderColor: "info.main",
                        },
                      },
                      "& .MuiInputBase-root.Mui-focused": {
                        boxShadow: "none",
                        borderColor: "transparent !important",
                      },
                    }}
                  >
                    <SoftInput
                      placeholder="Search by title, description, type, requester..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      icon={{ component: "search", direction: "left" }}
                    />
                  </SoftBox>
                  <IconButton
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    color={hasActiveFilters ? "info" : "default"}
                    title="Filter"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Icon>filter_list</Icon>
                  </IconButton>
                  <Popover
                    open={!!filterAnchorEl}
                    anchorEl={filterAnchorEl}
                    onClose={() => setFilterAnchorEl(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                      sx: (theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        backgroundImage: "none",
                        borderRadius: "1rem",
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "0 20px 40px rgba(15, 34, 58, 0.12)",
                        minWidth: 240,
                      }),
                    }}
                  >
                    <SoftBox p={2.5} minWidth={240}>
                      <SoftBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <SoftTypography
                          variant="button"
                          fontWeight="bold"
                          color="dark"
                        >
                          Filter by
                        </SoftTypography>
                        <SoftButton
                          size="small"
                          variant="text"
                          color="secondary"
                          onClick={() => {
                            setFilterType("");
                            setFilterOriginState("");
                            setFilterDestinationState("");
                            setFilterDueDate("");
                            setFilterUrgentOnly(false);
                          }}
                          disabled={!hasActiveFilters}
                          sx={{
                            visibility: hasActiveFilters ? "visible" : "hidden",
                            minWidth: 80,
                          }}
                        >
                          Clear filters
                        </SoftButton>
                      </SoftBox>
                      <SoftBox mb={1}>
                        <SoftTypography
                          variant="caption"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          Type
                        </SoftTypography>
                        <SoftSelect
                          placeholder="All types"
                          options={FILTER_TYPE_OPTIONS}
                          value={
                            FILTER_TYPE_OPTIONS.find(
                              (o) => o.value === filterType,
                            ) || null
                          }
                          onChange={(opt) => setFilterType(opt?.value ?? "")}
                          isClearable
                        />
                      </SoftBox>
                      <SoftBox mb={1}>
                        <SoftTypography
                          variant="caption"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          Origin state
                        </SoftTypography>
                        <SoftSelect
                          placeholder="All"
                          options={[
                            { value: "", label: "All" },
                            ...uniqueOriginStates.map((s) => ({
                              value: s,
                              label: s,
                            })),
                          ]}
                          value={
                            filterOriginState
                              ? {
                                  value: filterOriginState,
                                  label: filterOriginState,
                                }
                              : null
                          }
                          onChange={(opt) =>
                            setFilterOriginState(opt?.value ?? "")
                          }
                          isClearable
                        />
                      </SoftBox>
                      <SoftBox mb={1}>
                        <SoftTypography
                          variant="caption"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          Destination state
                        </SoftTypography>
                        <SoftSelect
                          placeholder="All"
                          options={[
                            { value: "", label: "All" },
                            ...uniqueDestinationStates.map((s) => ({
                              value: s,
                              label: s,
                            })),
                          ]}
                          value={
                            filterDestinationState
                              ? {
                                  value: filterDestinationState,
                                  label: filterDestinationState,
                                }
                              : null
                          }
                          onChange={(opt) =>
                            setFilterDestinationState(opt?.value ?? "")
                          }
                          isClearable
                        />
                      </SoftBox>
                      <SoftBox mb={1}>
                        <SoftTypography
                          variant="caption"
                          fontWeight="bold"
                          display="block"
                          mb={0.5}
                        >
                          Due date
                        </SoftTypography>
                        <SoftSelect
                          placeholder="All"
                          options={FILTER_DUE_DATE_OPTIONS}
                          value={
                            FILTER_DUE_DATE_OPTIONS.find(
                              (o) => o.value === filterDueDate,
                            ) || null
                          }
                          onChange={(opt) => setFilterDueDate(opt?.value ?? "")}
                          isClearable
                        />
                      </SoftBox>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!filterUrgentOnly}
                            onChange={(e) =>
                              setFilterUrgentOnly(e.target.checked)
                            }
                            size="small"
                            color="primary"
                            sx={{
                              "&.Mui-checked": {
                                color: "info.main",
                              },
                            }}
                          />
                        }
                        label={
                          <SoftTypography variant="button" fontWeight="medium">
                            Urgent only
                          </SoftTypography>
                        }
                        sx={{ ml: 0.5, alignItems: "center" }}
                      />
                    </SoftBox>
                  </Popover>
                </SoftBox>
                {filteredRequests.length === 0 ? (
                  <SoftBox py={4} textAlign="center">
                    <SoftTypography variant="body2" color="text">
                      No requests match your search or filters. Try different
                      criteria.
                    </SoftTypography>
                  </SoftBox>
                ) : (
                  <>
                    <Table
                      columns={requestColumns}
                      rows={requestRows}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                    <SoftBox
                      mt={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={2}
                    >
                      <SoftTypography
                        variant="button"
                        color="secondary"
                        fontWeight="regular"
                      >
                        Showing {filteredRequests.length}{" "}
                        {filteredRequests.length === 1
                          ? "request"
                          : "requests"}
                        {searchQuery.trim() || hasActiveFilters
                          ? " (filtered on this page)"
                          : ""}
                        {canGoNext || canGoPrevious
                          ? " · more available"
                          : ""}
                      </SoftTypography>
                      {canGoPrevious || canGoNext ? (
                        <SoftPagination variant="gradient" color="info">
                          <SoftPagination
                            item
                            onClick={handlePreviousPage}
                            disabled={!canGoPrevious || pageLoading}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_left
                            </Icon>
                          </SoftPagination>
                          <SoftTypography
                            variant="button"
                            fontWeight="medium"
                            mx={1}
                            sx={{ alignSelf: "center" }}
                          >
                            {pageLoading ? "Loading…" : "Prev / Next"}
                          </SoftTypography>
                          <SoftPagination
                            item
                            onClick={handleNextPage}
                            disabled={!canGoNext || pageLoading}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_right
                            </Icon>
                          </SoftPagination>
                        </SoftPagination>
                      ) : null}
                    </SoftBox>
                  </>
                )}
              </>
            )}
          </SoftBox>
        </Card>
      )}
      <Dialog
        open={!!pendingDeleteDraftRequest}
        onClose={() => {
          if (!deletingDraft) setPendingDeleteDraftRequest(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete draft request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete "{pendingDeleteDraftRequest?.title}"? This draft will no
            longer appear in your resource requests.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            variant="text"
            color="secondary"
            onClick={() => setPendingDeleteDraftRequest(null)}
            disabled={deletingDraft}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="error"
            onClick={handleDeleteDraft}
            disabled={deletingDraft}
          >
            {deletingDraft ? "Deleting..." : "Delete"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default RequestsPage;
